import React, { useEffect, useState } from "react";
import { Form, Input, Switch, DatePicker, Spin, message } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { API_METHODS } from "../../constants";
import useApi from "../../hooks/useApi";
import { isEmpty } from "../../generalFunctions";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const PRASAD_PRICE = 100;
const ADDON_PRICE = 300;
const ADDONS = [
  { type: "naivedya", label: "Naivedya" },
  { type: "bhojan_prasad", label: "Bhojan Prasad" },
];

const TRUST_BADGES = [
  { icon: "🛡️", label: "100% Authentic", sub: "Hereditary Shreepujak" },
  { icon: "🪔", label: "Spiritual Experience", sub: "Kolhapur tradition" },
];

function QtyControl({ value, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <button
        type="button"
        onClick={() => onChange(Math.max(0, value - 1))}
        disabled={value === 0}
        style={{
          width: 32, height: 32, borderRadius: 8,
          border: "1.5px solid #f97316", background: "white",
          color: "#f97316", fontSize: 18, fontWeight: 700,
          cursor: value === 0 ? "not-allowed" : "pointer",
          opacity: value === 0 ? 0.35 : 1,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >−</button>
      <span style={{ width: 20, textAlign: "center", fontWeight: 600, fontSize: 15 }}>{value}</span>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        style={{
          width: 32, height: 32, borderRadius: 8,
          border: "1.5px solid #f97316", background: "white",
          color: "#f97316", fontSize: 18, fontWeight: 700,
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >+</button>
    </div>
  );
}

function SectionCard({ title, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "white",
        borderRadius: 16,
        border: "1px solid #f3e8d8",
        padding: "20px 22px",
        marginBottom: 16,
        boxShadow: hovered ? "0 6px 24px rgba(249,115,22,0.13)" : "0 2px 10px rgba(0,0,0,0.05)",
        transform: hovered ? "translateY(-1px)" : "none",
        transition: "all 0.18s",
      }}
    >
      {title && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <div style={{ width: 4, height: 18, background: "linear-gradient(to bottom, #f97316, #dc2626)", borderRadius: 4 }} />
          <span style={{ fontWeight: 700, fontSize: 11, color: "#9a3412", letterSpacing: "0.1em", textTransform: "uppercase" }}>
            {title}
          </span>
        </div>
      )}
      {children}
    </div>
  );
}

function CountdownTimer({ startDate, endDate }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const now = new Date();
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  if (now >= start && now <= end) {
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#dcfce7", color: "#16a34a", padding: "8px 16px", borderRadius: 20, fontWeight: 600, fontSize: 14, marginBottom: 16 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#16a34a", display: "inline-block", animation: "mlx-pulse 1.2s infinite" }} />
        Event is happening now
      </div>
    );
  }

  const diff = start - now;
  if (diff <= 0) return null;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  const units = [
    { value: days, label: "Days" },
    { value: hours, label: "Hrs" },
    { value: minutes, label: "Min" },
    { value: seconds, label: "Sec" },
  ];

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 8, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}>
        Booking closes in
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {units.map(({ value, label }) => (
          <div key={label} style={{ textAlign: "center", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: "8px 12px", minWidth: 50 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#f97316", lineHeight: 1 }}>{String(value).padStart(2, "0")}</div>
            <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 3, fontWeight: 600 }}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  const [currentImage, setCurrentImage] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [addonQtys, setAddonQtys] = useState({ naivedya: 0, bhojan_prasad: 0 });
  const [prasad, setPrasad] = useState(false);
  const [form] = Form.useForm();

  const [eventResponse, eventRequest] = useApi(`/user/events/${slug}`, null, API_METHODS.GET);
  const [orderResponse, orderRequest] = useApi();
  const [verifyResponse, verifyRequest] = useApi();

  useEffect(() => {
    eventRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (!isEmpty(eventResponse) && !eventResponse.isLoading && eventResponse?.error) {
      navigate("/services", { replace: true });
    }
  }, [eventResponse, navigate]);

  const event = eventResponse?.data;

  useEffect(() => {
    if (isEmpty(orderResponse) || orderResponse.isLoading) return;
    if (orderResponse.isError || orderResponse.error) {
      messageApi.error(orderResponse.error || "Booking failed. Please try again.");
      return;
    }
    if (!orderResponse.data?.razorpay_order_id) return;

    const d = orderResponse.data;
    const openRazorpay = async () => {
      const loaded = await new Promise((resolve) => {
        if (window.Razorpay) { resolve(true); return; }
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
      });
      if (!loaded) { messageApi.error("Razorpay failed to load."); return; }

      new window.Razorpay({
        key: d.key,
        amount: d.amount * 100,
        currency: d.currency,
        name: "MahalaxmiPuja",
        description: d.event?.name || "Event Booking",
        order_id: d.razorpay_order_id,
        theme: { color: "#f97316" },
        prefill: {
          name: form.getFieldValue("name") || "",
          contact: form.getFieldValue("whatsapp_number") || "",
        },
        handler: (response) => {
          verifyRequest(
            {
              order_id: d.order_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            },
            API_METHODS.POST,
            "/user/orders/book-event/verify"
          );
        },
        modal: { ondismiss: () => messageApi.warning("Payment cancelled. You can retry anytime.") },
      }).open();
    };
    openRazorpay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderResponse]);

  useEffect(() => {
    if (isEmpty(verifyResponse) || verifyResponse.isLoading) return;
    if (verifyResponse.isError || verifyResponse.error) {
      messageApi.error("Payment verification failed. Please contact support.");
      return;
    }
    if (verifyResponse.data?.order_status === "confirmed") {
      navigate("/booking/success", {
        state: {
          order_number: verifyResponse.data.order_number,
          service_name: event?.heading,
          booking_date: form.getFieldValue("booking_date")?.format("YYYY-MM-DD"),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyResponse]);

  const packages = event
    ? [
        event.price_single && { key: "single", label: "Single", price: event.price_single },
        event.price_couple && { key: "couple", label: "Couple", price: event.price_couple },
        event.price_family && { key: "family", label: "Family", price: event.price_family },
      ].filter(Boolean)
    : [];

  const selectedPkg = packages.find((p) => p.key === selectedPackage);
  const addonTotal = ADDONS.reduce((s, a) => s + addonQtys[a.type] * ADDON_PRICE, 0);
  const activeAddons = ADDONS.filter((a) => addonQtys[a.type] > 0);
  const totalAmount = (selectedPkg?.price || 0) + addonTotal + (prasad ? PRASAD_PRICE : 0);
  const lowestPrice = packages.length ? Math.min(...packages.map((p) => p.price)) : null;
  const paying = orderResponse?.isLoading || verifyResponse?.isLoading;

  const disabledDate = (current) => {
    if (!current || !event) return true;
    const tomorrow = dayjs().tz("Asia/Kolkata").startOf("day").add(1, "day");
    if (current.isBefore(tomorrow)) return true;
    const start = dayjs(event.start_date).startOf("day");
    const end = dayjs(event.end_date).startOf("day");
    return current.isBefore(start) || current.isAfter(end);
  };

  const handlePayAndBook = async () => {
    if (!selectedPackage) {
      messageApi.error("Please select a package first");
      return;
    }
    try {
      const values = await form.validateFields();
      orderRequest(
        {
          event_id: event.id,
          package_type: selectedPackage,
          booking_date: values.booking_date.format("YYYY-MM-DD"),
          name: values.name,
          gotra: values.gotra || undefined,
          whatsapp_number: values.whatsapp_number,
          addons: ADDONS.filter((a) => addonQtys[a.type] > 0).map((a) => ({ type: a.type, qty: addonQtys[a.type] })),
          prasad_courier: prasad,
          ...(prasad && {
            prasad_name: values.prasad_name,
            prasad_address_line1: values.prasad_address_line1,
            prasad_address_line2: values.prasad_address_line2,
            prasad_city: values.prasad_city,
            prasad_state: values.prasad_state,
            prasad_pincode: values.prasad_pincode,
          }),
        },
        API_METHODS.POST,
        "/user/orders/book-event"
      );
    } catch {
      // form validation errors shown by Form.Item
    }
  };

  if (isEmpty(eventResponse) || eventResponse.isLoading || eventResponse?.error) {
    return (
      <>
        {contextHolder}
        <HeaderComponent />
        <div className="min-h-screen flex items-center justify-center">
          <Spin size="large" />
        </div>
        <Footer />
      </>
    );
  }

  if (!event) return null;

  const images = event.images || [];
  const dateLabel = event.start_date === event.end_date
    ? new Date(event.start_date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : `${new Date(event.start_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} – ${new Date(event.end_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <>
      {contextHolder}
      <HeaderComponent />

      <div style={{ background: "#faf7f4", minHeight: "100vh" }}>

        {/* Hero */}
        <div style={{ position: "relative", height: 260, overflow: "hidden" }}>
          {images.length > 0 ? (
            <>
              <img
                src={images[0].image_url}
                alt={event.heading}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.65) 100%)" }} />
            </>
          ) : (
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #f97316 0%, #dc2626 100%)" }} />
          )}

          {/* Back pill */}
          <button
            onClick={() => navigate(-1)}
            style={{
              position: "absolute", top: 18, left: 20, zIndex: 2,
              background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.4)",
              borderRadius: 999, color: "white", fontSize: 13, fontWeight: 600,
              padding: "6px 14px", cursor: "pointer", backdropFilter: "blur(4px)",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            ← Back
          </button>

          {/* Event label */}
          <div style={{
            position: "absolute", top: 18, left: "50%", transform: "translateX(-50%)", zIndex: 2,
            background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.4)",
            borderRadius: 999, color: "white", fontSize: 11, fontWeight: 700,
            padding: "4px 14px", letterSpacing: "0.1em", textTransform: "uppercase",
            backdropFilter: "blur(4px)",
          }}>
            🪔 Special Event
          </div>

          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 1, padding: "0 24px 24px" }}>
            <div style={{ maxWidth: 1200, margin: "0 auto" }}>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: "white", margin: "0 0 6px", lineHeight: 1.2, textShadow: "0 2px 12px rgba(0,0,0,0.3)" }}>
                {event.heading}
              </h1>
              {event.sub_heading && (
                <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 14, margin: "0 0 10px" }}>{event.sub_heading}</p>
              )}
              <div style={{ display: "inline-block", background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 999, padding: "5px 16px", fontSize: 13, fontWeight: 600, color: "white" }}>
                📅 {dateLabel}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 20px 64px", display: "flex", gap: 28, alignItems: "flex-start" }}>

          {/* LEFT: content */}
          <div style={{ flex: "1 1 0", minWidth: 0 }}>

            {/* Countdown */}
            <CountdownTimer startDate={event.start_date} endDate={event.end_date} />

            {/* Image carousel */}
            {images.length > 0 && (
              <div style={{ borderRadius: 20, overflow: "hidden", background: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", border: "1px solid #f3e8d8", marginBottom: 16 }}>
                <div style={{ position: "relative", aspectRatio: "16/9", background: "#fff7ed" }}>
                  <img
                    src={images[currentImage]?.image_url}
                    alt={event.heading}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  {images.length > 1 && (
                    <>
                      <button
                        style={{
                          position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                          background: "rgba(0,0,0,0.45)", color: "#fff", border: "none",
                          borderRadius: "50%", width: 36, height: 36, fontSize: 20,
                          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                        onClick={() => setCurrentImage((i) => (i - 1 + images.length) % images.length)}
                      >‹</button>
                      <button
                        style={{
                          position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                          background: "rgba(0,0,0,0.45)", color: "#fff", border: "none",
                          borderRadius: "50%", width: 36, height: 36, fontSize: 20,
                          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                        onClick={() => setCurrentImage((i) => (i + 1) % images.length)}
                      >›</button>
                    </>
                  )}
                </div>
                {images.length > 1 && (
                  <div style={{ display: "flex", gap: 8, padding: "10px 12px 12px" }}>
                    {images.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentImage(i)}
                        style={{
                          width: 56, height: 40, flexShrink: 0, borderRadius: 8,
                          overflow: "hidden", border: i === currentImage ? "2px solid #f97316" : "2px solid transparent",
                          padding: 0, cursor: "pointer", background: "none",
                        }}
                      >
                        <img src={img.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* About */}
            {event.description && (
              <SectionCard title="About This Event">
                <p style={{ color: "#374151", lineHeight: 1.75, whiteSpace: "pre-line", margin: 0, fontSize: 14 }}>
                  {event.description}
                </p>
              </SectionCard>
            )}

            {/* Trust badges */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginBottom: 16 }}>
              {TRUST_BADGES.map((b) => (
                <div key={b.label} style={{ background: "white", borderRadius: 14, border: "1px solid #f3e8d8", padding: "16px 14px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                  <div style={{ fontSize: 24, marginBottom: 6 }}>{b.icon}</div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: "#1f2937", marginBottom: 3 }}>{b.label}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>{b.sub}</div>
                </div>
              ))}
            </div>

            {/* Temple info */}
            <SectionCard title="Venue">
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{ width: 44, height: 44, background: "linear-gradient(135deg, #f97316, #dc2626)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
                  🛕
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: "#1f2937", fontSize: 14, marginBottom: 3 }}>Shri Mahalaxmi Ambabai Mandir</div>
                  <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}>
                    Kolhapur, Maharashtra<br />
                    Performed by 54th generation hereditary Shreepujak
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          {/* RIGHT: sticky booking sidebar */}
          <div style={{ width: 400, flexShrink: 0, position: "sticky", top: 88 }}>

            {/* Price header card */}
            <div style={{ borderRadius: 20, overflow: "hidden", boxShadow: "0 8px 32px rgba(249,115,22,0.20)", marginBottom: 0 }}>
              <div style={{ background: "linear-gradient(135deg, #f97316, #dc2626)", padding: "20px 22px 18px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.75)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>
                  Book Your Spot
                </div>
                <div style={{ fontWeight: 800, fontSize: 22, color: "white", lineHeight: 1.2 }}>
                  {event.heading}
                </div>
                {lowestPrice && (
                  <div style={{ marginTop: 8, display: "inline-block", background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 999, padding: "4px 14px", fontSize: 14, fontWeight: 700, color: "white" }}>
                    from ₹{lowestPrice.toLocaleString("en-IN")}
                  </div>
                )}
              </div>

              {/* Form body */}
              <div style={{ background: "white", padding: "20px 22px 22px" }}>
                <Form form={form} layout="vertical" requiredMark={false}>

                  {/* Package selector */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9a3412", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 3, height: 14, background: "linear-gradient(to bottom, #f97316, #dc2626)", borderRadius: 4 }} />
                      🎁 Choose Package
                    </div>
                    {packages.length === 0 && (
                      <p style={{ color: "#9ca3af", fontSize: 13 }}>No packages available.</p>
                    )}
                    {packages.map((pkg, i) => (
                      <div
                        key={pkg.key}
                        onClick={() => setSelectedPackage(pkg.key)}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "12px 14px",
                          marginBottom: i < packages.length - 1 ? 8 : 0,
                          borderRadius: 12, cursor: "pointer",
                          border: selectedPackage === pkg.key ? "2px solid #f97316" : "2px solid #f3f4f6",
                          background: selectedPackage === pkg.key ? "#fff7ed" : "#fafafa",
                          transition: "all 0.15s",
                        }}
                      >
                        <span style={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>{pkg.label}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontWeight: 700, fontSize: 17, color: "#f97316" }}>₹{pkg.price}</span>
                          <div style={{
                            width: 20, height: 20, borderRadius: "50%",
                            border: selectedPackage === pkg.key ? "2px solid #f97316" : "2px solid #d1d5db",
                            background: selectedPackage === pkg.key ? "#f97316" : "transparent",
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                          }}>
                            {selectedPackage === pkg.key && (
                              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                                <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Divider */}
                  <div style={{ height: 1, background: "#f3f4f6", margin: "0 0 18px" }} />

                  {/* Personal details */}
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9a3412", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 3, height: 14, background: "linear-gradient(to bottom, #f97316, #dc2626)", borderRadius: 4 }} />
                    👤 Your Details
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500, fontSize: 13, color: "#374151" }}>Name <span style={{ color: "#ef4444" }}>*</span></span>}
                      name="name"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Input placeholder="Full name" />
                    </Form.Item>
                    <Form.Item
                      label={<span style={{ fontWeight: 500, fontSize: 13, color: "#374151" }}>Gotra</span>}
                      name="gotra"
                    >
                      <Input placeholder="Optional" />
                    </Form.Item>
                  </div>

                  <Form.Item
                    label={<span style={{ fontWeight: 500, fontSize: 13, color: "#374151" }}>WhatsApp Number <span style={{ color: "#ef4444" }}>*</span></span>}
                    name="whatsapp_number"
                    rules={[
                      { required: true, message: "Required" },
                      { pattern: /^[6-9]\d{9}$/, message: "Valid 10-digit number" },
                    ]}
                  >
                    <Input placeholder="10-digit mobile" maxLength={10} prefix={<span style={{ color: "#9ca3af", fontSize: 13 }}>+91</span>} />
                  </Form.Item>

                  {/* Date */}
                  <Form.Item
                    label={<span style={{ fontWeight: 500, fontSize: 13, color: "#374151" }}>📅 Puja Date <span style={{ color: "#ef4444" }}>*</span></span>}
                    name="booking_date"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      disabledDate={disabledDate}
                      placeholder={`Select (${event.start_date}${event.start_date !== event.end_date ? ` – ${event.end_date}` : ""})`}
                      format="DD MMM YYYY"
                    />
                  </Form.Item>

                  {/* Add-ons */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9a3412", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 3, height: 14, background: "linear-gradient(to bottom, #f97316, #dc2626)", borderRadius: 4 }} />
                      ✨ Add-ons <span style={{ fontWeight: 500, fontSize: 10, color: "#9ca3af", textTransform: "none", letterSpacing: 0, marginLeft: 4 }}>(+₹{ADDON_PRICE} each)</span>
                    </div>
                    {ADDONS.map((addon) => (
                      <div key={addon.type} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                        <span style={{ fontWeight: 500, fontSize: 13, color: "#374151" }}>{addon.label}</span>
                        <QtyControl
                          value={addonQtys[addon.type]}
                          onChange={(v) => setAddonQtys((p) => ({ ...p, [addon.type]: v }))}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Prasad by Courier */}
                  <div style={{ borderRadius: 12, border: "1px solid #fed7aa", background: "#fff7ed", padding: "14px 16px", marginBottom: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>🚚 Prasad by Courier</div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>Receive prasad at your doorstep (+₹{PRASAD_PRICE})</div>
                      </div>
                      <Switch
                        checked={prasad}
                        onChange={(val) => {
                          setPrasad(val);
                          if (val) form.setFieldValue("prasad_name", form.getFieldValue("name") || "");
                          if (!val) form.resetFields(["prasad_name","prasad_address_line1","prasad_address_line2","prasad_city","prasad_state","prasad_pincode"]);
                        }}
                        style={prasad ? { backgroundColor: "#f97316" } : {}}
                      />
                    </div>
                    {prasad && (
                      <div style={{ marginTop: 14 }}>
                        <Form.Item label={<span style={{ fontWeight: 500, fontSize: 13 }}>Recipient Name <span style={{ color: "#ef4444" }}>*</span></span>} name="prasad_name" rules={[{ required: true, message: "Required" }]}>
                          <Input placeholder="Name on delivery" />
                        </Form.Item>
                        <Form.Item label={<span style={{ fontWeight: 500, fontSize: 13 }}>Address Line 1 <span style={{ color: "#ef4444" }}>*</span></span>} name="prasad_address_line1" rules={[{ required: true, message: "Required" }]}>
                          <Input placeholder="House / Flat / Building" />
                        </Form.Item>
                        <Form.Item label={<span style={{ fontWeight: 500, fontSize: 13 }}>Address Line 2</span>} name="prasad_address_line2">
                          <Input placeholder="Street / Area (optional)" />
                        </Form.Item>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                          <Form.Item label={<span style={{ fontWeight: 500, fontSize: 13 }}>City <span style={{ color: "#ef4444" }}>*</span></span>} name="prasad_city" rules={[{ required: true, message: "Required" }]}>
                            <Input placeholder="City" />
                          </Form.Item>
                          <Form.Item label={<span style={{ fontWeight: 500, fontSize: 13 }}>State <span style={{ color: "#ef4444" }}>*</span></span>} name="prasad_state" rules={[{ required: true, message: "Required" }]}>
                            <Input placeholder="State" />
                          </Form.Item>
                        </div>
                        <Form.Item label={<span style={{ fontWeight: 500, fontSize: 13 }}>Pincode <span style={{ color: "#ef4444" }}>*</span></span>} name="prasad_pincode" rules={[{ required: true, message: "Required" }, { pattern: /^\d{6}$/, message: "6-digit pincode" }]}>
                          <Input placeholder="6-digit pincode" maxLength={6} />
                        </Form.Item>
                      </div>
                    )}
                  </div>

                  {/* Price Summary */}
                  {selectedPkg && (
                    <div style={{ borderRadius: 12, background: "#f9fafb", border: "1px solid #e5e7eb", padding: "14px 16px", marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Price Summary</div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                        <span style={{ color: "#374151", fontSize: 13 }}>{selectedPkg.label} Package</span>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>₹{selectedPkg.price}</span>
                      </div>
                      {activeAddons.map((a) => (
                        <div key={a.type} style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                          <span style={{ color: "#374151", fontSize: 13 }}>{a.label} × {addonQtys[a.type]}</span>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>₹{addonQtys[a.type] * ADDON_PRICE}</span>
                        </div>
                      ))}
                      {prasad && (
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                          <span style={{ color: "#374151", fontSize: 13 }}>Prasad Courier</span>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>₹{PRASAD_PRICE}</span>
                        </div>
                      )}
                      <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #e5e7eb", paddingTop: 10, marginTop: 6 }}>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>Total</span>
                        <span style={{
                          fontWeight: 800, fontSize: 20,
                          background: "linear-gradient(to right, #f97316, #dc2626)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}>₹{totalAmount.toLocaleString("en-IN")}</span>
                      </div>
                    </div>
                  )}

                  {/* Trust strip */}
                  <div style={{ textAlign: "center", padding: "0 0 14px", color: "#9ca3af", fontSize: 11, letterSpacing: "0.02em" }}>
                    🔒 Secure Payment &nbsp;·&nbsp; ⚡ Instant Booking
                  </div>

                  {/* Pay button */}
                  <button
                    type="button"
                    disabled={packages.length === 0 || paying}
                    onClick={handlePayAndBook}
                    style={{
                      width: "100%", height: 52,
                      background: (!selectedPkg || packages.length === 0 || paying)
                        ? "#e5e7eb"
                        : "linear-gradient(to right, #f97316, #dc2626)",
                      border: "none", borderRadius: 14,
                      color: (!selectedPkg || packages.length === 0 || paying) ? "#9ca3af" : "white",
                      fontSize: 16, fontWeight: 800,
                      cursor: (!selectedPkg || packages.length === 0 || paying) ? "not-allowed" : "pointer",
                      boxShadow: (selectedPkg && !paying) ? "0 4px 18px rgba(249,115,22,0.45)" : "none",
                      animation: (selectedPkg && !paying) ? "payGlow 2s ease-in-out infinite" : "none",
                      transition: "all 0.2s",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {paying ? "Processing…" : selectedPkg ? `Pay & Book — ₹${totalAmount.toLocaleString("en-IN")}` : "Select a Package to Continue"}
                  </button>

                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <style>{`
        @keyframes mlx-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes payGlow {
          0%, 100% { box-shadow: 0 4px 20px rgba(249,115,22,0.45); }
          50% { box-shadow: 0 6px 32px rgba(249,115,22,0.70); }
        }
      `}</style>
    </>
  );
}

export default EventPage;
